/**
 * Product Card Component Styles
 *
 * Adaptive theming — see styles.ts for rationale.
 */

import { ADAPTIVE_TOKENS } from '../theme';

export const PRODUCT_CARD_STYLES = `
  :host {
    ${ADAPTIVE_TOKENS}
    /* Card-local shadow variants */
    --ksp-shadow-sm:
      0 1px 2px color-mix(in srgb, currentColor 6%, transparent),
      0 1px 3px color-mix(in srgb, currentColor 4%, transparent);
    --ksp-shadow-lg:
      0 4px 8px color-mix(in srgb, currentColor 5%, transparent),
      0 18px 40px -16px color-mix(in srgb, currentColor 14%, transparent);
    /* Slightly rounder card than cart page */
    --ksp-radius-md: var(--ks-host-radius-md, 12px);

    color: inherit;
    opacity: 1 !important;
    font-family: var(--ksp-font-body);
  }

  .ks-card {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: var(--ksp-radius-md);
    transition: transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1),
                box-shadow 320ms cubic-bezier(0.22, 0.61, 0.36, 1),
                border-color 220ms ease;
    cursor: pointer;
  }

  /* Card style variants */
  .ks-card--flat { background: transparent; }

  .ks-card--elevated {
    background: var(--ksp-card-bg);
    box-shadow: var(--ksp-shadow-sm);
  }

  .ks-card--elevated:hover { box-shadow: var(--ksp-shadow-lg); }

  .ks-card--outlined {
    background: transparent;
    border: 1px solid var(--ksp-hairline);
  }

  .ks-card--outlined:hover { border-color: var(--ksp-accent); }

  .ks-card--glass {
    background: color-mix(in srgb, currentColor 4%, transparent);
    backdrop-filter: blur(10px);
    border: 1px solid var(--ksp-hairline);
  }

  /* Hover animations */
  :host([data-ks-hover="lift"]) .ks-card:hover { transform: translateY(-4px); }
  :host([data-ks-hover="scale"]) .ks-card:hover { transform: scale(1.02); }
  :host([data-ks-hover="glow"]) .ks-card:hover {
    box-shadow: 0 0 24px color-mix(in srgb, var(--ksp-accent) 28%, transparent);
  }

  /* Image container */
  .ks-card-image {
    position: relative;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    background: var(--ksp-card-bg-strong);
  }

  .ks-card-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 420ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .ks-card:hover .ks-card-image img { transform: scale(1.05); }

  /* Placeholder for missing images */
  .ks-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--ksp-card-bg-strong);
  }

  .ks-placeholder-letter {
    font-family: var(--ksp-font-display);
    font-size: 3rem;
    font-weight: 600;
    color: var(--ksp-muted-2);
    line-height: 1;
    text-transform: uppercase;
    user-select: none;
  }

  .ks-placeholder--hidden { display: none; }

  /* Badges */
  .ks-badge {
    position: absolute;
    top: 0.75rem;
    left: 0.75rem;
    padding: 0.25rem 0.625rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    border-radius: 999px;
    background: var(--ksp-accent);
    color: var(--ksp-accent-ink);
  }

  .ks-badge--sale { background: #d94a3d; color: #ffffff; }
  .ks-badge--new { background: #0a8a65; color: #ffffff; }
  .ks-badge--event { background: #7c3aed; color: #ffffff; }
  .ks-badge--subscription { background: #0891b2; color: #ffffff; }
  .ks-badge--live_event { background: #7c3aed; color: #ffffff; }
  .ks-badge--out-of-stock {
    background: color-mix(in srgb, currentColor 70%, transparent);
    color: var(--ksp-accent-ink);
  }

  /* Wishlist button */
  .ks-wishlist {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: color-mix(in srgb, white 88%, transparent);
    border: none;
    border-radius: 50%;
    cursor: pointer;
    opacity: 0;
    transform: scale(0.8);
    transition: opacity 220ms ease, transform 220ms cubic-bezier(0.22, 0.61, 0.36, 1), background 180ms ease;
    box-shadow: var(--ksp-shadow-sm);
  }

  .ks-card:hover .ks-wishlist {
    opacity: 1;
    transform: scale(1);
  }

  .ks-wishlist:hover { background: #ffffff; }

  .ks-wishlist svg {
    width: 1rem;
    height: 1rem;
    color: #4a4a4a;
    transition: color 180ms ease, fill 180ms ease;
  }

  .ks-wishlist:hover svg,
  .ks-wishlist.active svg {
    color: #d94a3d;
    fill: #d94a3d;
  }

  /* Card body */
  .ks-card-body {
    padding: 0.875rem 0.75rem 0.75rem;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  /* Title */
  .ks-card-title {
    font-family: var(--ksp-font-display);
    font-size: 0.9375rem;
    font-weight: 500;
    color: inherit;
    margin: 0 0 0.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    letter-spacing: -0.005em;
  }

  /* Description */
  .ks-card-desc {
    font-size: 0.8125rem;
    color: var(--ksp-muted);
    margin: 0 0 0.5rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
  }

  /* Price */
  .ks-card-price {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    margin-top: auto;
  }

  .ks-price {
    font-family: var(--ksp-font-display);
    font-size: 1rem;
    font-weight: 600;
    color: inherit;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.005em;
  }

  .ks-price-original {
    font-size: 0.8125rem;
    color: var(--ksp-muted-2);
    text-decoration: line-through;
    font-variant-numeric: tabular-nums;
  }

  /* Rating */
  .ks-card-rating {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-top: 0.25rem;
  }

  .ks-stars { display: flex; gap: 1px; }

  .ks-star {
    width: 0.875rem;
    height: 0.875rem;
    color: #e8a838;
  }

  .ks-star--empty { color: var(--ksp-hairline-strong); }

  .ks-rating-count {
    font-size: 0.75rem;
    color: var(--ksp-muted);
  }

  /* Quick buy button */
  .ks-quick-buy {
    margin-top: 0.75rem;
    width: 100%;
    padding: 0.625rem;
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    background: var(--ksp-accent);
    border: none;
    border-radius: var(--ksp-btn-radius);
    color: var(--ksp-accent-ink);
    cursor: pointer;
    font-family: inherit;
    transition: filter 180ms ease, transform 100ms ease;
  }

  .ks-quick-buy:hover:not(:disabled) { filter: brightness(1.08); }
  .ks-quick-buy:active:not(:disabled) { transform: scale(0.98); }

  .ks-quick-buy:disabled {
    background: var(--ksp-hairline-strong);
    color: var(--ksp-muted);
    cursor: not-allowed;
    opacity: 0.9;
  }

  /* Out-of-stock dimming */
  :host([data-ks-out-of-stock]) .ks-card {
    opacity: 0.6;
    filter: grayscale(40%);
  }

  :host([data-ks-out-of-stock]) .ks-card:hover {
    opacity: 0.8;
    filter: grayscale(20%);
  }

  /* List layout variant */
  :host([layout="list"]) .ks-card { flex-direction: row; }

  :host([layout="list"]) .ks-card-image {
    width: 140px;
    flex-shrink: 0;
    aspect-ratio: 1;
  }

  :host([layout="list"]) .ks-card-body { padding: 1rem; }

  :host([layout="list"]) .ks-card-title { white-space: normal; }
`;

export default PRODUCT_CARD_STYLES;
